/**
 * Standard barometric air density model.
 *
 * ρ(h) = ρ₀ × exp(-h / 8000m) where ρ₀ = 1.225 kg/m³ at sea level standard.
 *
 * Good to ±2% from sea level to 12,000 ft — adequate for archery.
 *
 * IMPORTANT — units: this module returns density in **slug/ft³** for use by
 * the trajectory integrator (which works in US-imperial units). The kg/m³
 * intermediate is converted before returning. The Environment override is
 * named `airDensityKgM3` (the natural human-readable unit) and converted
 * here.
 *
 * Conversion: 1 kg/m³ = 0.001940 slug/ft³
 *             1.225 kg/m³ (sea level) = 0.002378 slug/ft³
 */

import type { Environment } from './types';

const SEA_LEVEL_DENSITY_KG_M3 = 1.225;
const SCALE_HEIGHT_M = 8000;
const FT_PER_M = 3.28084;
const KG_M3_TO_SLUG_FT3 = 0.001940;

export function airDensityFromAltitudeFt(altitudeFt: number): number {
  const altitudeM = altitudeFt / FT_PER_M;
  const kgM3 = SEA_LEVEL_DENSITY_KG_M3 * Math.exp(-altitudeM / SCALE_HEIGHT_M);
  return kgM3 * KG_M3_TO_SLUG_FT3;
}

export function airDensityForEnvironment(env: Pick<Environment, 'altitudeFt' | 'airDensityKgM3'>): number {
  if (env.airDensityKgM3 != null && env.airDensityKgM3 > 0) {
    return env.airDensityKgM3 * KG_M3_TO_SLUG_FT3;
  }
  return airDensityFromAltitudeFt(env.altitudeFt);
}
